import { FormActions } from "../../components/common/FormActions";
import { IFormSection, TFormHookParams } from "../../types/common";
import { API_ENFORCEMENT_TEAMS } from "../../utils/apis";
import { CAN_MANAGE_ENFORCEMENT_TEAMS } from "../../utils/constants";
import {
  createDeactivateAction,
  createDeleteAction,
  createReactivateAction,
  createViewEdit,
  formatDateTime,
  getForm,
  getRegister,
} from "../../utils/helpers";
import { URL_ENFORCEMENT_TEAMS } from "../../utils/urls";
import {
  auditFields,
  showNumber,
  useDistrictOptions,
  userFullName,
} from "../assets/assetUtils";
import {
  ActiveTag,
  CapacityTag,
  useDeploymentPermissions,
} from "./deploymentUtils";

// Backend accepts letters, digits and spaces only
const TEAM_NAME_PATTERN = /^[A-Za-z0-9 ]+$/;

export const useEnforcementTeams = () => {
  const can = useDeploymentPermissions();
  const canManage = can(CAN_MANAGE_ENFORCEMENT_TEAMS);

  const tableColumns = () => [
    { id: "name", header: "Team Name", filter: { type: "text" } },
    { id: "city_name", header: "City", filter: { type: "text" } },
    { id: "members", header: "Members / Capacity" },
    {
      id: "is_active",
      header: "Status",
      filter: {
        type: "select",
        options: [
          { id: "true", name: "Active" },
          { id: "false", name: "Deactivated" },
        ],
      },
    },
    { id: "created_by", header: "Created By" },
    { id: "created_at", header: "Created At" },
    { id: "actions", header: "Actions" },
  ];

  const manageActions = (item: any) => {
    if (!canManage) return [];
    const url = `${API_ENFORCEMENT_TEAMS}${item?.id}/`;

    return [
      item?.is_active
        ? createDeactivateAction(
            `${url}deactivate/`,
            item?.name,
            {},
            {
              showReasonField: true,
              method: "POST",
            },
          )
        : createReactivateAction(`${url}reactivate/`, item?.name),
      ...(item?.current_member_count
        ? []
        : [createDeleteAction(url, item?.name)]),
    ];
  };

  const tableData = (data: any[]) =>
    data?.map((item: any) => ({
      ...item,
      ...auditFields(item),
      members: <CapacityTag item={item} />,
      is_active: <ActiveTag active={item?.is_active} />,
      actions: (
        <FormActions
          actions={[
            ...createViewEdit(URL_ENFORCEMENT_TEAMS, item?.id, canManage),
            ...manageActions(item),
          ]}
        />
      ),
    }));

  const viewData = (item: any) => ({
    sections: [
      {
        title: "Team Details",
        fields: {
          name: item?.name,
          city: item?.city_name,
          capacity: showNumber(item?.capacity),
          current_members: showNumber(item?.current_member_count),
          availability: <CapacityTag item={item} />,
          status: <ActiveTag active={item?.is_active} />,
        },
      },
      {
        title: "Deactivation",
        visible: item?.is_active === false,
        fields: {
          deactivated_by:
            userFullName(item?.deactivated_by) ?? item?.deactivated_by,
          deactivated_at: formatDateTime(item?.deactivated_at),
          reason: item?.deactivation_reason,
        },
      },
      { title: "Record", fields: auditFields(item) },
    ],
  });

  return {
    tableTitle: "Enforcement Teams",
    tableColumns,
    tableData,
    viewData,
    formRoute: canManage ? getForm(URL_ENFORCEMENT_TEAMS) : undefined,
    createTitle: "New Enforcement Team",
    activityFilter: "enforcement_teams",
  };
};

export const useEnforcementTeamsForm = ({
  id,
  setError,
}: TFormHookParams = {}) => {
  const districts = useDistrictOptions();

  const sections: IFormSection[] = [
    {
      title: "Team Details",
      fields: [
        {
          field_name: "name",
          label: "Team Name",
          type: "text",
          props: { required: !id, placeholder: "e.g. Group A" },
        },
        {
          field_name: "city",
          label: "City",
          type: "select",
          options_data: districts.options,
          loading: districts.loading,
          props: { required: !id, placeholder: "Select city" },
        },
        {
          field_name: "capacity",
          label: "Capacity",
          type: "number",
          props: {
            required: !id,
            placeholder: "Maximum number of officers",
          },
        },
      ],
    },
  ];

  const onSubmit = (payload: any) => {
    const name = payload?.name?.trim();
    if (name !== undefined && !TEAM_NAME_PATTERN.test(name)) {
      setError?.({ name: "Use letters, numbers and spaces only." });
      return;
    }

    // updates only send the fields that changed
    const params = Object.fromEntries(
      Object.entries({
        name,
        city: payload?.city,
        capacity:
          payload?.capacity !== undefined && payload?.capacity !== ""
            ? Number(payload.capacity)
            : undefined,
      }).filter(([, value]) => value !== undefined),
    );

    return { ...(id ? { id } : {}), api: API_ENFORCEMENT_TEAMS, params };
  };

  return {
    sections,
    title: "Enforcement Team",
    redirect: getRegister(URL_ENFORCEMENT_TEAMS),
    onSubmit,
  };
};
